import React from "react";
import { Button } from "@/components/ui/button";
import { Phone, Sparkles, ShieldCheck, Star } from "lucide-react";
import { LOGO_URL, BUSINESS, STATS } from "@/data/content";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 -z-10 bg-black" />
      <div className="absolute inset-0 -z-10 bg-grid opacity-70" />
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#00BFFF]/10 blur-[120px] -z-10" />
      <div className="absolute -bottom-40 -right-32 w-[600px] h-[600px] rounded-full bg-[#C4FF00]/10 blur-[140px] -z-10" />

      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-800 bg-black/50 backdrop-blur mb-6">
            <Sparkles className="h-4 w-4 text-[#C4FF00]" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-300">
              South Florida · West Palm Beach → Miami
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter text-white leading-[0.95]">
            Pressure washed.
            <br />
            <span className="text-[#C4FF00] text-glow-lime">Softly cleaned.</span>
            <br />
            <span className="text-[#00BFFF]">Spotless.</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg text-zinc-400 leading-relaxed">
            Exterior cleaning done the right way. From soft-wash house exteriors and
            roof treatments to driveways, solar panels and full fleet detailing —
            we bring showroom-clean results to every South Florida property.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#contact">
              <Button
                className="bg-[#C4FF00] text-black hover:bg-[#D4FF33] font-bold uppercase tracking-widest px-8 h-14 rounded-full text-sm glow-lime"
                data-testid="hero-cta-quote"
              >
                Get Your Free Quote
              </Button>
            </a>
            <a href={BUSINESS.phoneHref}>
              <Button
                variant="outline"
                className="bg-transparent border-2 border-[#00BFFF] text-[#00BFFF] hover:bg-[#00BFFF]/10 hover:text-[#00BFFF] font-bold uppercase tracking-widest px-8 h-14 rounded-full text-sm"
                data-testid="hero-cta-call"
              >
                <Phone className="h-4 w-4 mr-2" />
                {BUSINESS.phone}
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs uppercase tracking-widest font-bold text-zinc-500">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#C4FF00]" />
              Fully Insured
            </span>
            <span className="flex items-center gap-2">
              <Star className="h-4 w-4 text-[#C4FF00] fill-[#C4FF00]" />
              4.9 Average Rating
            </span>
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#C4FF00]" />
              Eco-Safe Detergents
            </span>
          </div>
        </div>

        {/* Right — logo showcase */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-square max-w-xl mx-auto">
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="Spotless Water Works Logo"
                className="w-full h-auto object-contain float scale-110"
                style={{ mixBlendMode: "screen" }}
                data-testid="hero-logo"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="mx-auto max-w-7xl px-6 md:px-10 mt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="bg-black p-6 md:p-8 flex flex-col items-center justify-center text-center"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-display text-3xl md:text-4xl text-white uppercase">
                {s.value}
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500 font-bold">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
